import { useEffect, useState } from "react";
import { Menu, X, Phone, User } from "lucide-react";
import { Link } from "react-router-dom";
import { BUSINESS, LOGO_URL } from "@/data/services";
import { trackPhoneClick } from "@/lib/analytics";
import PromoBanner from "@/components/PromoBanner";
import { useCustomerAuth } from "@/lib/customerAuth";

const GIFT_CARD_URL =
  "https://my.loopz.io/gift-card/7005b6c0-267f-485a-8dc7-667d3243ba5b?locale=en-CA";

const NAV = [
  { label: "Services", href: "/services", route: true },
  { label: "Service Areas", href: "/service-areas", route: true },
  { label: "Work", href: "/#work" },
  { label: "Reviews", href: "/#reviews" },
  { label: "Blog", href: "/blog", route: true },
  { label: "Book", href: "/#booking" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { customer, login } = useCustomerAuth();

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 20);
    on();
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ease-out ${
        scrolled
          ? "bg-[#0A0A0C]/75 backdrop-blur-2xl border-b border-white/[0.06]"
          : "bg-transparent"
      }`}
    >
      <PromoBanner />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-6 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center group flex-1 min-w-0" data-testid="brand-logo">
          <img
            src={LOGO_URL}
            alt="Darkside Detailing — Mobile Auto Detailing Halifax NS"
            className="max-h-28 sm:max-h-28 lg:max-h-32 w-auto max-w-full object-contain object-left group-hover:scale-[1.02] transition-transform duration-300 ease-out"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) =>
            n.route ? (
              <Link
                key={n.href}
                to={n.href}
                className="text-sm text-white/70 hover:text-white link-underline transition-colors duration-300 ease-out"
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.href}
                href={n.href}
                className="text-sm text-white/70 hover:text-white link-underline transition-colors duration-300 ease-out"
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </a>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={GIFT_CARD_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="header-giftcard-btn"
            className="px-4 py-2.5 border border-white/15 text-white/90 hover:border-[#E30613] hover:text-[#E30613] text-sm font-medium rounded-sm transition-colors duration-300 ease-out"
          >
            Gift Card
          </a>
          <a
            href={BUSINESS.phoneHref}
            onClick={() => trackPhoneClick({ location: "header" })}
            className="flex items-center gap-2 text-sm text-white/80 hover:text-[#E30613] transition-colors duration-300 ease-out"
            data-testid="header-phone"
          >
            <Phone className="w-4 h-4" />
            {BUSINESS.phone}
          </a>
          {customer ? (
            <Link
              to="/account"
              className="inline-flex items-center gap-2 px-3 py-2.5 border border-white/15 text-white/90 hover:border-[#E30613] hover:text-[#E30613] text-sm font-medium rounded-sm transition-colors duration-300 ease-out max-w-[160px]"
              data-testid="header-account-btn"
              title="Your account"
            >
              {customer.picture ? (
                <img src={customer.picture} alt="" className="w-5 h-5 rounded-full object-cover" />
              ) : (
                <User className="w-4 h-4" />
              )}
              <span className="truncate">{customer.name?.split(" ")[0] || "Account"}</span>
            </Link>
          ) : (
            <button
              onClick={login}
              className="inline-flex items-center gap-2 px-3 py-2.5 border border-white/15 text-white/90 hover:border-[#E30613] hover:text-[#E30613] text-sm font-medium rounded-sm transition-colors duration-300 ease-out"
              data-testid="header-signin-btn"
            >
              <User className="w-4 h-4" />
              Sign in
            </button>
          )}
          <a
            href="/#booking"
            data-testid="header-book-btn"
            className="px-5 py-2.5 rounded-sm bg-[#E30613] text-white text-sm font-semibold hover:bg-[#B00510] active:scale-95 transition-[background-color,transform] duration-300 ease-out shadow-[0_0_20px_rgba(227,6,19,0.35)]"
          >
            Book Now
          </a>
        </div>

        {/* Mobile Call button (prominent, always visible) */}
        <a
          href={BUSINESS.phoneHref}
          onClick={() => trackPhoneClick({ location: "mobile" })}
          className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-sm bg-[#E30613] text-white text-xs font-semibold shadow-[0_0_15px_rgba(227,6,19,0.3)]"
          data-testid="mobile-call-btn"
        >
          <Phone className="w-3.5 h-3.5" />
          Call
        </a>

        <button
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden p-2 -mr-1 text-white flex-shrink-0"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden border-t border-white/[0.06] bg-[#0A0A0C]/95 backdrop-blur-2xl">
          <nav className="px-6 py-6 flex flex-col gap-4">
            {NAV.map((n) =>
              n.route ? (
                <Link
                  key={n.href}
                  to={n.href}
                  onClick={() => setOpen(false)}
                  className="text-base text-white/80 hover:text-[#E30613] transition-colors duration-300 ease-out"
                >
                  {n.label}
                </Link>
              ) : (
                <a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  className="text-base text-white/80 hover:text-[#E30613] transition-colors duration-300 ease-out"
                >
                  {n.label}
                </a>
              )
            )}
            <a
              href={GIFT_CARD_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              className="text-base text-white/80 hover:text-[#E30613] transition-colors duration-300 ease-out"
            >
              Gift Card
            </a>
            {customer ? (
              <Link
                to="/account"
                onClick={() => setOpen(false)}
                className="text-base text-white/80 hover:text-[#E30613] transition-colors duration-300 ease-out inline-flex items-center gap-2"
                data-testid="mobile-account-link"
              >
                <User className="w-4 h-4" />
                My account ({customer.name?.split(" ")[0] || "You"})
              </Link>
            ) : (
              <button
                onClick={() => { setOpen(false); login(); }}
                className="text-base text-white/80 hover:text-[#E30613] transition-colors duration-300 ease-out inline-flex items-center gap-2 text-left"
                data-testid="mobile-signin-btn"
              >
                <User className="w-4 h-4" />
                Sign in
              </button>
            )}
            <a
              href="/#booking"
              onClick={() => setOpen(false)}
              className="mt-2 px-5 py-3 rounded-sm bg-[#E30613] text-white text-sm font-semibold text-center"
            >
              Book Now
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
